<template>
  <!-- Edit event dialog -->
  <v-dialog v-model="dialog" width="auto" min-width="60%" max-width="90%">
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span class="text-h6 font-weight-bold">{{ $t('event.edit.modal.title') }}</span>
        <v-btn icon variant="text" @click="dialog = false">
          <v-icon :icon="mdiClose" />
        </v-btn>
      </v-card-title>

      <v-form ref="form" validate-on="submit">
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12" md="7">
                <v-row>
                  <v-col cols="12">
                    <v-select
                      :label="$t('event.new.modal.type')"
                      :items="eventTypesList"
                      item-title="label"
                      item-value="id"
                      v-model="selectedTypeId"
                      :rules="[v => !!v || t('event.new.modal.validation.type')]"
                      :return-object="false"
                    />
                  </v-col>
                </v-row>

                <v-row align="center">
                  <v-col>
                    <date-picker-input-old
                      v-model="dateStart"
                      :label="$t('event.new.modal.date-start')"
                      :rules="[v => !!v || t('event.new.modal.validation.date-start')]"
                    />
                  </v-col>
                  <v-col v-if="!selectedType?.time_selection_type">
                    <time-picker-input
                      :label="$t('appointment.new.modal.timepicker-at')"
                      :minutesIncrement="15"
                      :minutesGridIncrement="15"
                      v-model="timeStart"
                      :rules="[v => !!v || t('event.new.modal.validation.time-start')]"
                    />
                  </v-col>
                  <v-col v-if="selectedType?.time_selection_type === 'morning_afternoon'">
                    <v-radio-group v-model="dateStartPeriod" inline density="compact" hide-details>
                      <v-radio :label="$t('event.new.modal.morning')" value="morning" />
                      <v-radio :label="$t('event.new.modal.afternoon')" value="afternoon" />
                    </v-radio-group>
                  </v-col>
                </v-row>

                <v-row align="center">
                  <v-col>
                    <date-picker-input-old
                      v-model="dateEnd"
                      :label="$t('event.new.modal.date-end')"
                      :rules="dateEndRules"
                      :min="dateStart"
                    />
                  </v-col>
                  <v-col v-if="!selectedType?.time_selection_type">
                    <time-picker-input
                      :label="$t('appointment.new.modal.timepicker-at')"
                      :minutesIncrement="15"
                      :minutesGridIncrement="15"
                      v-model="timeEnd"
                      :rules="[v => !!v || t('event.new.modal.validation.time-end')]"
                    />
                  </v-col>
                  <v-col v-if="selectedType?.time_selection_type === 'morning_afternoon'">
                    <v-radio-group v-model="dateEndPeriod" inline density="compact" hide-details>
                      <v-radio :label="$t('event.new.modal.morning')" value="morning" />
                      <v-radio :label="$t('event.new.modal.afternoon')" value="afternoon" />
                    </v-radio-group>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12">
                    <v-text-field
                      :label="$t('event.new.modal.title-field')"
                      v-model="eventTitle"
                      :rules="[v => !!v || t('event.new.modal.validation.title')]"
                    />
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12">
                    <v-textarea
                      :label="$t('event.new.modal.description')"
                      v-model="eventDescription"
                      rows="3"
                    />
                  </v-col>
                </v-row>
              </v-col>

              <v-col cols="12" md="5">
                <div v-if="selectedType?.is_day_off">
                  <div v-if="loadingBalance" class="d-flex align-center mb-2">
                    <v-progress-circular indeterminate size="20" class="mr-2" />
                  </div>
                  <div v-else-if="daysOffBalance !== null" class="mb-2">
                    <div class="font-weight-bold">
                      {{ t('event.new.modal.days-off-balance', { balance: daysOffBalance }) }}
                    </div>
                    <div v-if="provisionalBalance !== null" class="text-body-2 text-medium-emphasis">
                      {{ t('event.new.modal.provisional-balance', { balance: provisionalBalance }) }}
                    </div>
                  </div>
                </div>

                <div class="mt-4">
                  <div class="font-weight-bold mb-1">{{ $t('event.new.modal.pending-requests') }}</div>
                  <div v-if="pendingRequests.length === 0" class="text-body-2 text-medium-emphasis">
                    {{ $t('event.new.modal.no-pending-requests') }}
                  </div>
                  <div v-for="req in pendingRequests" :key="req.id" class="text-body-2">
                    - {{ t('event.new.modal.pending-request-item', {
                        start: `${dateHandler.convertUnixTimestampToHumanDate(req.date_start)}  ${dateHandler.convertUnixTimestampToHumanTime(req.date_start)}`,
                        end: `${dateHandler.convertUnixTimestampToHumanDate(req.date_end)}  ${dateHandler.convertUnixTimestampToHumanTime(req.date_end)}`,
                        days: req._totalDays || 1
                      }) }}
                  </div>
                </div>

                <v-alert v-if="errorMessage" type="error" variant="tonal" class="mt-4" closable @click:close="errorMessage = null">
                  {{ errorMessage }}
                </v-alert>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            :prepend-icon="mdiClose"
            color="grey-darken-2"
            variant="tonal"
            @click="dialog = false"
          >
            {{ $t('global.btn.cancel') }}
          </v-btn>
          <v-btn
            :prepend-icon="mdiCheck"
            v-bind="useButtonStyle"
            @click="validate"
            :loading="saving"
          >
            {{ $t('event.edit.modal.save') }}
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { mdiClose, mdiCheck } from '@mdi/js';
import { useI18n } from 'vue-i18n';
import { useApplicationStore } from '@/stores/applicationStore';
import { useStoreManager } from '@/stores/storeManager';
import DatePickerInputOld from '@/components/common/DatePickerInputOld.vue';
import TimePickerInput from '@/components/common/TimePickerInput.vue';
import dateHandler from '@/utils/dateHandler';
import eventApiService from '@/services/eventApi';
import moment from 'moment';
import { useButtonStyle } from '@/utils/appStyleHandler';

const { t } = useI18n();
const applicationStore = useApplicationStore();
const storeManager = useStoreManager();

const emit = defineEmits(['updated']);

const props = defineProps({
  craEvent: {
    type: Object,
    required: true,
  },
});

const dialog = ref(false);
const form = ref(null);
const saving = ref(false);
const errorMessage = ref(null);

const selectedTypeId = ref(null);
const dateStart = ref(null);
const dateEnd = ref(null);
const timeStart = ref(null);
const timeEnd = ref(null);
const dateStartPeriod = ref('morning');
const dateEndPeriod = ref('afternoon');
const eventTitle = ref('');
const eventDescription = ref('');

const daysOffBalance = ref(null);
const loadingBalance = ref(false);

const configurations = computed(() =>
  storeManager.getFromStoreByCategory('configuration', 'configurations')
);

const eventTypes = computed(() => {
  if (!configurations.value) return {};
  for (const conf of Object.values(configurations.value)) {
    if (conf?.planningCraEventTypes && Object.keys(conf.planningCraEventTypes).length > 0) {
      return conf.planningCraEventTypes;
    }
  }
  return {};
});

const eventTypesList = computed(() => Object.values(eventTypes.value));

const selectedType = computed(() => {
  if (!selectedTypeId.value) return null;
  return eventTypes.value[selectedTypeId.value] || null;
});

const dateEndRules = computed(() => [
  (v) => !!v || t('event.new.modal.validation.date-end'),
]);

const planningCras = computed(() =>
  storeManager.getFromStoreByCategory('mission', 'planningCras')
);

/**
 * Calculate days in 0.5 steps based on start/end hours.
 */
function calculateHalfDays(startUnix, endUnix) {
  const s = moment.unix(startUnix);
  const e = moment.unix(endUnix);
  const startDate = s.clone().startOf('day');
  const endDate = e.clone().startOf('day');
  const totalCalendarDays = endDate.diff(startDate, 'days') + 1;

  let days = 0;
  for (let i = 0; i < totalCalendarDays; i++) {
    const current = startDate.clone().add(i, 'days');
    const isFirstDay = i === 0;
    const isLastDay = i === totalCalendarDays - 1;

    const dayStart = isFirstDay ? s.hour() + s.minute() / 60 : 8;
    const dayEnd = isLastDay ? e.hour() + e.minute() / 60 : 17.5;

    const hasMorning = dayStart < 12.5 && dayEnd > 8;
    const hasAfternoon = dayEnd > 14 && dayStart < 17.5;

    if (hasMorning) days += 0.5;
    if (hasAfternoon) days += 0.5;
  }
  return Math.max(0.5, days);
}

const pendingRequests = computed(() => {
  if (!planningCras.value) return [];
  return Object.values(planningCras.value).filter(
    (cra) => cra.status === null || cra.status === 'request'
  ).map((cra) => {
    const ds = parseInt(cra.date_start);
    const de = parseInt(cra.date_end);
    const totalDays = calculateHalfDays(ds, de);
    return { ...cra, date_start: ds, date_end: de, _totalDays: totalDays };
  });
});

const provisionalBalance = computed(() => {
  if (daysOffBalance.value === null) return null;
  const pendingDaysOff = pendingRequests.value
    .filter((req) => {
      const type = eventTypes.value[req.planning_cra_event_type?.id];
      return type?.is_day_off;
    })
    .reduce((sum, req) => sum + (req._totalDays || 1), 0);
  return daysOffBalance.value - pendingDaysOff;
});

watch(selectedType, async (newType) => {
  if (newType?.is_day_off) {
    await fetchDaysOffBalance();
  } else {
    daysOffBalance.value = null;
  }
});

/**
 * Fetch the days-off balance via the app backend.
 */
async function fetchDaysOffBalance() {
  loadingBalance.value = true;
  try {
    const data = await eventApiService.getDaysOffBalance();
    daysOffBalance.value = data.current_days_off;
  } catch (err) {
    console.error('[EventAPI] Failed to fetch days off balance:', err.message);
  } finally {
    loadingBalance.value = false;
  }
}

/**
 * Open the edit modal and pre-fill fields from the event data.
 */
function open() {
  const event = props.craEvent;
  selectedTypeId.value = event.planning_cra_event_type?.id || null;
  eventTitle.value = event.title || '';
  eventDescription.value = event.description || '';

  const startMoment = moment.unix(parseInt(event.date_start));
  const endMoment = moment.unix(parseInt(event.date_end));

  dateStart.value = startMoment.format('YYYY-MM-DD');
  dateEnd.value = endMoment.format('YYYY-MM-DD');

  // Use selectedType from config store as it's always reliable,
  // fallback to event's embedded type for older data
  const resolvedType = selectedType.value || event.planning_cra_event_type;
  const timeSelectionType = resolvedType?.time_selection_type;

  if (!timeSelectionType) {
    // Free time selection: set time pickers
    timeStart.value = startMoment.toDate();
    timeEnd.value = endMoment.toDate();
  } else if (timeSelectionType === 'morning_afternoon') {
    // Determine morning/afternoon from the stored hour
    dateStartPeriod.value = startMoment.hour() < 13 ? 'morning' : 'afternoon';
    dateEndPeriod.value = endMoment.hour() <= 13 ? 'morning' : 'afternoon';
  }

  errorMessage.value = null;
  dialog.value = true;

  const resolvedTypeForBalance = selectedType.value || event.planning_cra_event_type;
  if (resolvedTypeForBalance?.is_day_off) {
    fetchDaysOffBalance();
  }
}

/**
 * Compute unix timestamps for date_start and date_end based on time_selection_type.
 */
function computeTimestamps() {
  const type = selectedType.value;
  let startMoment, endMoment;

  if (!type?.time_selection_type) {
    const ts = timeStart.value;
    const te = timeEnd.value;
    startMoment = moment(new Date(dateStart.value)).startOf('day')
      .add(moment(ts).hours(), 'hours').add(moment(ts).minutes(), 'minutes');
    endMoment = moment(new Date(dateEnd.value)).startOf('day')
      .add(moment(te).hours(), 'hours').add(moment(te).minutes(), 'minutes');
  } else if (type.time_selection_type === 'morning_afternoon') {
    const startHour = dateStartPeriod.value === 'morning' ? 8 : 14;
    const endHour = dateEndPeriod.value === 'morning' ? 12 : 17;
    const endMin = 30;
    startMoment = moment(new Date(dateStart.value)).startOf('day').add(startHour, 'hours');
    endMoment = moment(new Date(dateEnd.value)).startOf('day').add(endHour, 'hours').add(endMin, 'minutes');
  } else {
    // days_only
    startMoment = moment(new Date(dateStart.value)).startOf('day').add(8, 'hours');
    endMoment = moment(new Date(dateEnd.value)).startOf('day').add(17, 'hours').add(30, 'minutes');
  }

  return {
    date_start: startMoment.unix().toString(),
    date_end: endMoment.unix().toString(),
  };
}

async function validate() {
  const { valid } = await form.value.validate();
  if (!valid) return;

  const type = selectedType.value;

  if (type?.is_day_off) {
    try {
      saving.value = true;
      const timestamps = computeTimestamps();
      const startDt = moment.unix(parseInt(timestamps.date_start)).format('YYYY-MM-DD HH:mm:ss');
      const endDt = moment.unix(parseInt(timestamps.date_end)).format('YYYY-MM-DD HH:mm:ss');
      const checkResult = await eventApiService.checkDaysOff(startDt, endDt);
      if (!checkResult.accepted) {
        errorMessage.value = t('event.new.modal.insufficient-balance');
        saving.value = false;
        return;
      }
    } catch (err) {
      errorMessage.value = err.response?.data?.message || err.message;
      saving.value = false;
      return;
    }
  }

  await saveEvent();
}

async function saveEvent() {
  saving.value = true;
  const timestamps = computeTimestamps();
  const type = selectedType.value;

  const apiPayload = {
    planning_cra_event_type_id: parseInt(type.id),
    date_start: moment.unix(parseInt(timestamps.date_start)).format('YYYY-MM-DD HH:mm:ss'),
    date_end: moment.unix(parseInt(timestamps.date_end)).format('YYYY-MM-DD HH:mm:ss'),
    title: eventTitle.value,
    description: eventDescription.value || null,
    date_start_morning_afternoon: type.time_selection_type === 'morning_afternoon' ? dateStartPeriod.value : null,
    date_end_morning_afternoon: type.time_selection_type === 'morning_afternoon' ? dateEndPeriod.value : null,
  };

  try {
    const eventId = props.craEvent.id;
    const result = await eventApiService.updateEvent(eventId, apiPayload);

    const responseData = result?.data || result;
    const backendId = responseData?.id || result?.id;

    if (!backendId) {
      errorMessage.value = result?.error || responseData?.error || t('event.edit.modal.update-error');
      return;
    }

    // Update the event in the store
    const storeKey = props.craEvent._storeKey;
    if (storeKey) {
      const updatedEvent = {
        ...props.craEvent,
        ...timestamps,
        planning_cra_event_type: {
          id: type.id,
          label: type.label,
          time_selection_type: type.time_selection_type,
          is_day_off: type.is_day_off,
          has_payroll_impact: type.has_payroll_impact,
          color_futur: type.color_futur,
          color_past: type.color_past,
        },
        title: eventTitle.value,
        description: eventDescription.value,
      };
      // Remove internal fields before storing
      delete updatedEvent._storeKey;
      delete updatedEvent._type;
      delete updatedEvent._isMultiDay;
      delete updatedEvent._dayIndex;
      delete updatedEvent._totalDays;

      storeManager.update('mission', 'planning_cras', storeKey, updatedEvent);

      // Force reactivity hack
      const missionStore = storeManager.storeReferences.mission;
      const userId = applicationStore.userDetails?.id;
      if (missionStore.data[userId]?.planning_cras) {
        missionStore.data[userId].planning_cras = { ...missionStore.data[userId].planning_cras };
      }
    }

    dialog.value = false;
    emit('updated');
  } catch (err) {
    // Extract error message from backend response (e.g. 409 Insufficient days off)
    const responseError = err.response?.data?.data?.error || err.response?.data?.error;
    errorMessage.value = responseError || err.message || t('event.edit.modal.update-error');
  } finally {
    saving.value = false;
  }
}

defineExpose({ open });
</script>



